import { EditTodoIcon } from '@/assets/icons/info-icons/editTodo';
import { IInfoIcon } from '@/assets/icons/info-icons/i-info';
import { InfoCircleIcon } from '@/assets/static/icons/info-circle';
import CheckBox from '@/components/basicComponents/checkbox';
import { EllipsisWithTooltip } from '@/components/basicComponents/ellipsisWithTooltip';
import Select from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { Tooltip } from '@/components/basicComponents/tooltip';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import Suitability from '@/components/investors-page/investorDetailsPage/tabs/beginAllocationTab/table/suitability';
import Amount from '@/components/pendingCommitments-page/table/cell/amount';
import ClientsAdvisor from '@/components/pendingCommitments-page/table/cell/clientsAdvisor';
import { AnchorPopover } from '@/components/table/anchor-drop-down';
import NoResults from '@/components/table/noResults';
import RedirectTitleCell from '@/components/table/redirectTitleCell';
import { TableRowCell, TableRowWrapper, TextCell } from '@/components/table/styles';
import guid from '@/shared/utils/guid';
import { themeVar } from '@/styles/theme';
import styled, { css } from 'styled-components';
import { columns } from '../constants';
import { AllocatorPageMode, ExtendedBulkAllocation } from '../types';
import PrevStatus from './cell/prevStatus';
import SuitabilityDetails, { SuitabilityWrapper } from './cell/suitabilityDetails';

const getDecisionSelectOptions = (row: ExtendedBulkAllocation) => {
  const isDecisionReasonExistInOverride = row?.override?.some((item) => item === row.decisionReason);
  const selectOptions = row?.override?.map((item) => createSimpleSelectOption(item)) || [];
  // return selectOptions;
  return isDecisionReasonExistInOverride ? selectOptions : [createSimpleSelectOption(row.decisionReason), ...selectOptions];
};

interface OpenOpportunitiesTableBodyProps {
  bulkAllocations: ExtendedBulkAllocation[];
  mode: AllocatorPageMode;
  selectRow: (row: ExtendedBulkAllocation) => void;
  handleChangeMode: (mode: AllocatorPageMode) => void;
  openModalWindow: (type: string, row: ExtendedBulkAllocation) => void;
  updateDecisionReason: (id: string, value: string) => void;
  openInvestorDetailsPage: ({ investorId, entityId, isGroup }: { investorId: string; entityId?: string; isGroup?: boolean }) => Promise<void>;
}

const TableBody = ({
  bulkAllocations,
  mode,
  selectRow,
  handleChangeMode,
  openModalWindow,
  updateDecisionReason,
  openInvestorDetailsPage
}: OpenOpportunitiesTableBodyProps) => {
  if (!bulkAllocations.length) {
    return mode === AllocatorPageMode.VIEW ? (
      <NoResults />
    ) : (
      <NoTasksWrapper>
        Nice work! There are no more tasks assigned to you.
        <RedirectTitleCell color={themeVar('font.action')} onClick={() => handleChangeMode(AllocatorPageMode.VIEW)}>
          Exit “My Tasks” to view all
        </RedirectTitleCell>
      </NoTasksWrapper>
    );
  }

  return (
    <>
      {bulkAllocations.map((row) => (
        <TableRowWrapper key={row.legalEntity.id}>
          {mode === AllocatorPageMode.EDIT && (
            <TableRowCell>
              <CheckboxWrapper id="input" onClick={() => selectRow(row)}>
                <CheckBox id="input" isActive={row.checked} />
              </CheckboxWrapper>
            </TableRowCell>
          )}
          <TableRowCell width={columns[0].width}>
            <EntityTableRowCell>
              <RedirectTitleCell
                id="actionCurioText"
                onClick={() => {
                  const { legalEntity, group } = row || {};
                  const { id, family, groupId } = legalEntity || {};

                  const entityIdToNavigate = !!groupId && group ? group.primaryEntity.id : id;
                  openInvestorDetailsPage({ investorId: family?.id, entityId: entityIdToNavigate, isGroup: !!groupId });
                }}
              >
                {row?.group && row?.selectedGroupedLegalEntity ? row?.group?.name : row?.legalEntity?.entityName}
              </RedirectTitleCell>
              {row?.selectedGroupedLegalEntity && (
                <PrimaryEntityWrapper
                  isEditMode={mode === AllocatorPageMode.EDIT}
                  onClick={() => mode === AllocatorPageMode.EDIT && openModalWindow('edit-amount', row)}
                >
                  <EllipsisWithTooltip title={row?.selectedGroupedLegalEntity?.entityName}>
                    <p>{row?.selectedGroupedLegalEntity?.entityName}</p>
                  </EllipsisWithTooltip>
                  {mode === AllocatorPageMode.EDIT && (
                    <div>
                      <EditTodoIcon width={12} height={10} fill={themeVar('context.warning')} />
                    </div>
                  )}
                </PrimaryEntityWrapper>
              )}
            </EntityTableRowCell>
          </TableRowCell>
          <TableRowCell width={columns[1].width}>
            <ClientsAdvisor
              onRedirect={() => openInvestorDetailsPage({ investorId: row.legalEntity?.family.id })}
              familyName={row.legalEntity?.family.name}
              advisor={
                row.legalEntity?.advisor
                  ? row.legalEntity?.advisor?.initials || `${row.legalEntity?.advisor?.firstName} ${row.legalEntity?.advisor?.lastName}`
                  : null
              }
            />
          </TableRowCell>
          <TableRowCell width={columns[2].width}>
            <TextCell>
              <FormattingTooltip zIndex={1000}>{row.legalEntity?.overallCapacity}</FormattingTooltip>
            </TextCell>
          </TableRowCell>
          <TableRowCell width={columns[3].width}>
            <TextCell>
              <FormattingTooltip zIndex={1000}>{row.legalEntity?.assetClassCapacity}</FormattingTooltip>
            </TextCell>
          </TableRowCell>
          <TableRowCell width={columns[4].width}>
            <AnchorPopover
              showLeft
              zIndex={100}
              rowBody={
                <SuitabilityWrapper>
                  <Suitability suitability={row.suitability === 'Suitable' ? 'Meets Criteria' : row.suitability} width="80" />
                  {row.legalEntity.ignoreBiteSize ||
                    (((row.investment.type === 'Direct' && row.committedCapital !== row.legalEntity.directBiteSize) ||
                      (row.investment.type === 'Fund' && row.committedCapital !== row.legalEntity.fundBiteSize)) && (
                      <Tooltip title="Differs from default bite size">
                        <IInfoIcon fill={themeVar('context.warning')} style={{ position: 'relative', top: '3px' }} />
                      </Tooltip>
                    ))}
                </SuitabilityWrapper>
              }
              title={row.suitability === 'Suitable' ? 'Meets Criteria' : row.suitability}
            >
              <SuitabilityDetails data={row?.suitabilityCriteria || []} key={guid()} />
            </AnchorPopover>
          </TableRowCell>
          <TableRowCell width={columns[5].width}>
            <TextCell>
              {mode === AllocatorPageMode.VIEW ? (
                <FormattingTooltip zIndex={1000}>{+row.committedCapital}</FormattingTooltip>
              ) : (
                <Amount showChangeAmountButton={true} amount={+row.committedCapital} openWindow={() => openModalWindow('edit-amount', row)} />
              )}
            </TextCell>
          </TableRowCell>
          <TableRowCell width={columns[6].width}>
            <DecisionReasonWrapper mode={mode}>
              {mode === AllocatorPageMode.VIEW ? (
                <TextCell>{row.decisionReason}</TextCell>
              ) : (
                <>
                  {row.decisionReason && (
                    <Select
                      value={row?.decisionReason || ''}
                      setValue={(value) => updateDecisionReason(row.legalEntity?.id, value)}
                      options={getDecisionSelectOptions(row)}
                      styleType={!row?.decisionReason ? 'disabled' : 'default'}
                      size="sm"
                    />
                  )}
                </>
              )}
              {row.decisionReason === 'Show All' && (
                <TooltipWrapper id="infoWrap">
                  <Tooltip
                    title="This investor is configured to see both recommended strategies and those that are not recommended. To override this default, choose one of the “No” options."
                    maxContentWidth={300}
                  >
                    <InfoCircleIcon fill={themeVar('font.disabled')} style={{ position: 'relative', top: '3px' }} />
                  </Tooltip>
                </TooltipWrapper>
              )}
            </DecisionReasonWrapper>
          </TableRowCell>
          <TableRowCell width={columns[7].width}>
            <PrevStatus amount={row.existingCommitment} data={row.preAllocationStatus} />
          </TableRowCell>
        </TableRowWrapper>
      ))}
    </>
  );
};

export default TableBody;

const NoTasksWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  color: ${themeVar('font.base')};
`;

const CheckboxWrapper = styled.div`
  margin-right: 15px;
`;

const EntityTableRowCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const PrimaryEntityWrapper = styled.div<{ isEditMode: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  font-weight: 600;
  color: ${themeVar('context.warning')};
  cursor: ${({ isEditMode }) => (isEditMode ? 'pointer' : 'default')};
`;

const DecisionReasonWrapper = styled.div<{ mode: AllocatorPageMode }>`
  width: 100%;
  ${({ mode }) =>
    mode === AllocatorPageMode.VIEW
      ? css`
          display: flex;
          gap: 8px;
          align-items: center;
        `
      : css`
          display: grid;
          grid-template-columns: 1fr 20px;
          grid-gap: 8px;
          align-items: center;
        `}
`;

const TooltipWrapper = styled.div`
  opacity: 0;
`;
